/*
ResponsiveVideo

Define breakpoints in markup using the `data-responsive-video` attribute in markup. This expects a JSON object where the key names a breakpoint and the value represents a min-width value for that breakpoint.

Example:
```
<video
    data-responsive-video="{
        'small': 320,
        'large': 640
    }"
></video>
```

A <source> child element of the <video> should have `data-src-{breakpoint-name}` attributes whose values point to the source that corresponds with the {breakpoint-name}. Given the breakpoints from the <video> example above, our <source> element might look like this:
```
<source
    data-src-small="video-320.mpg"
    data-src-large="video-640.mpg"
/>
```

Example all together:
```
<video
    data-responsive-video="{
        'small': 320,
        'large': 640
    }"
>
    <source
        data-src-small="video-320.mpg"
        data-src-large="video-640.mpg"
    />
</video>
```

These are based on mobile-first min-width media queries. In the above example, if the screen size were smaller than 320, no sources would match and no video would play (the poster would be shown if present).

*/

function ResponsiveVideo (video) {
    this.video = video;
    this.breakpoints = JSON.parse(video.dataset.responsiveVideo.replace(/'/g, '"'));
    this.currentBreakpoint = null;
    this.playPromise = null;

    this.enableUI()
        .testBreakpoint()
        .handleBreakpointChange(this.currentBreakpoint);
};

ResponsiveVideo.prototype.enableUI = function () {
    window.addEventListener('resize', this.testBreakpoint.bind(this));
    return this;
};

ResponsiveVideo.prototype.testBreakpoint = function() {
    var w = window.innerWidth;
    var candidates = [];
    var candidatesSizes = [];
    var ret = [];

    for (var breakpoint in this.breakpoints) {
        if (this.breakpoints.hasOwnProperty(breakpoint)) {
            if (w >= this.breakpoints[breakpoint]) {
                candidates.push(breakpoint);
                candidatesSizes.push(this.breakpoints[breakpoint]);
            }
        }
    }

    // Duplicate of candidates to be pared down and returned
    ret = candidates.slice(0);

    for (var i = 0; i < candidates.length; i++) {
        for (var j = 0; j < candidates.length; j++) {
            if (candidatesSizes[i] < candidatesSizes[j]) {
                ret.splice(ret.indexOf(candidates[i]), 1);
                break;
            }
        }
    }

    if (ret[0] !== this.currentBreakpoint) {
        this.handleBreakpointChange(ret[0]);
    }

    this.currentBreakpoint = ret[0];

    return this;
};

ResponsiveVideo.prototype.handleBreakpointChange = function (breakpoint) {
    if (!breakpoint) {
        this.reset();
        return this;
    }

    var targetSource = this.video.querySelector('source');
    var targetAttr = 'src' + normalizeDataAttribute(breakpoint);
    var src = targetSource.dataset[targetAttr];
    var handleAsyncPause = handleAsyncPause.bind(this);

    if (!src) {
        this.reset();
        return this;
    }

    targetSource.setAttribute('src', src);

    this.playPromise
        ? this.playPromise.then(handleAsyncPause)
        : handleAsyncPause();

    return this;

    function handleAsyncPause () {
        var self = this;
        var wasPaused = this.video.paused;

        this.video.pause();
        this.video.load();

        if (!wasPaused) {
            this.playPromise = this.video.play()
                .finally(function () {
                    self.playPromise = null;
                })
                .catch(function (e) {
                    // e
                });
        }
    }
};

ResponsiveVideo.prototype.reset = function () {
    var self = this;
    this.video.querySelector('source').removeAttribute('src');

    if (this.playPromise) {
        this.playPromise.then(function () {
            self.video.pause();
            self.video.load();
        });
        return;
    }

    this.video.pause();
    this.video.load();
};

function normalizeDataAttribute (str) {
    return str
        .toLowerCase()
        .replace(/-{2,}/g, '-')
        .split('-')
        .map(function (segment) {
            return segment.substring(0, 1).toUpperCase() + segment.substring(1)
        })
        .join('');
}

document
    .querySelectorAll('video[data-responsive-video]')
    .forEach(function (video) { new ResponsiveVideo(video) });
